import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { DateInputBR } from '@components/DateInputBR';
import { Card, CardHeader, CardContent } from '@components/Card';
import { holidayService } from '@services/holidayService';
import { HolidayFormData } from '@/types/holiday';
import { validateHolidayForm } from './holidayUtils';

export const HolidayFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<HolidayFormData>({
    date: '',
    description: '',
    is_national_holiday: true,
    is_active: true,
  });

  useEffect(() => {
    if (isEditing && id) {
      loadData(parseInt(id));
    }
  }, [id, isEditing]);

  const loadData = async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await holidayService.getById(itemId);
      setFormData({
        date: data.date,
        description: data.description,
        is_national_holiday: data.is_national_holiday ?? true,
        is_active: data.is_active ?? true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof HolidayFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSave = async () => {
    const validationErrors = validateHolidayForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setSaving(true);
      setError(null);

      const payload: HolidayFormData = {
        ...formData,
        description: formData.description.trim(),
      };

      if (isEditing && id) {
        await holidayService.update(parseInt(id), payload);
      } else {
        await holidayService.create(payload);
      }

      navigate('/settings/general/holidays');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/general/holidays')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/settings/general/holidays')}
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Feriado' : 'Novo Feriado'}
          </h1>
          <p className="text-slate-600">
            {isEditing
              ? 'Altere as informações do feriado'
              : 'Preencha os dados para cadastrar um novo feriado'}
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados do Feriado" />
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Data <span className="text-red-500">*</span>
              </label>
              <DateInputBR
                className={`input-field ${errors.date ? 'border-red-400 focus:ring-red-400' : ''}`}
                value={formData.date}
                onChange={(v) => updateField('date', v)}
                placeholder="dd/mm/aaaa"
              />
              {errors.date && (
                <p className="text-xs text-red-600 mt-1">{errors.date}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <Input
                label="Descrição *"
                placeholder="Ex: Natal"
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                error={errors.description}
                maxLength={150}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Abrangência
              </label>
              <select
                className="input w-full text-sm"
                value={formData.is_national_holiday ? 'true' : 'false'}
                onChange={(e) => updateField('is_national_holiday', e.target.value === 'true')}
              >
                <option value="true">Nacional</option>
                <option value="false">Estadual / Municipal</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Indica se o feriado é nacional ou estadual/municipal
              </p>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" />
                <span className="ml-3 text-sm font-medium text-slate-700">
                  {formData.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </label>
              <p className="text-xs text-slate-400">
                Feriados inativos não são considerados nos cálculos de prazos
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/settings/general/holidays')}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          icon={<Save size={20} />}
        >
          {saving
            ? 'Salvando...'
            : isEditing
            ? 'Salvar Alterações'
            : 'Criar Feriado'}
        </Button>
      </div>
    </div>
  );
};
